<template>
  <div class="guest-checkout">
    <el-form :model="form" :rules="rules" ref="guestForm" label-position="top">
      <h3 class="section-title">{{ $t('message.home.contactInfo') || 'Contact Information' }}</h3>

      <el-form-item :label="$t('message.home.email') || 'Email'" prop="email">
        <el-input v-model="form.email" placeholder="example@email.com" @blur="onEmailBlur"></el-input>
        <p class="email-hint" v-if="emailExists">
          {{ $t('message.home.emailRegistered') || 'This email is already registered. You can login after checkout.' }}
        </p>
      </el-form-item>

      <h3 class="section-title">{{ $t('message.home.shippingAddress') || 'Shipping Address' }}</h3>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item :label="$t('message.home.consignee') || 'Full Name'" prop="consignee">
            <el-input v-model="form.consignee" :placeholder="$t('message.home.enterName') || 'Enter full name'"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('message.home.phone') || 'Phone'" prop="phone">
            <div class="form-phone">
              <el-input v-model="form.phone" :placeholder="$t('message.home.enterPhone') || 'Enter phone number'" :maxlength="20"></el-input>
              <VueCountryIntl
                v-model="form.areaCode"
                schema="popover"
                :searchInputPlaceholder="$t('message.home.pleaseSetAreaCode') || 'Search area code'"
                noDataText="No data found"
              >
                <div class="area-code flex-between" slot="reference">
                  <span>+{{ form.areaCode || '' }}</span>
                  <i class="el-icon-caret-bottom"></i>
                </div>
              </VueCountryIntl>
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <div class="check_address">
        <el-form-item :label="$t('message.home.country') || 'Country'" prop="regionId">
          <el-select v-model="currentCountry" :placeholder="$t('message.home.nation') || 'Country'" filterable>
            <el-option
              v-for="(item, index) in countryList"
              :key="item.id"
              :value="item.id"
              :label="item.name"
              @click.native="countryListChange(index)"
            >
              <span>{{ item.name }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('message.home.province') || 'State/Province'">
          <el-select v-model="currentProvince" :placeholder="$t('message.home.state') || 'State/Province'" filterable>
            <el-option
              v-for="(item, index) in provinces"
              :key="item.id"
              :label="item.name"
              :value="item.id"
              @click.native="provinceListChange(index)"
            >
              <span>{{ item.name }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('message.home.city') || 'City'">
          <el-select v-model="currentCity" :placeholder="$t('message.home.city') || 'City'" filterable>
            <el-option
              v-for="(item, index) in cities"
              :key="item.id"
              :label="item.name"
              :value="item.id"
              @click.native="cityListChange(index)"
            >
              <span>{{ item.name }}</span>
            </el-option>
          </el-select>
        </el-form-item>
      </div>

      <el-form-item :label="$t('message.home.postCode') || 'Zip Code'">
        <el-input v-model="form.zipCode" :placeholder="$t('message.home.postCode') || 'Zip Code'" :maxlength="9"></el-input>
      </el-form-item>

      <el-form-item :label="$t('message.home.detailAddress') || 'Detail Address'" prop="address">
        <el-input v-model="form.address" type="textarea" :rows="2"
          :placeholder="$t('message.home.enterAddress') || 'Street address, apartment, suite, etc.'"></el-input>
      </el-form-item>

      <h3 class="section-title">{{ $t('message.home.paymentInfo') || 'Payment Information' }}</h3>

      <el-form-item :label="$t('message.home.cardNumber') || 'Card Number'" prop="cardNumber">
        <el-input v-model="form.cardNumber" placeholder="0000 0000 0000 0000" :maxlength="19"></el-input>
      </el-form-item>

      <el-form-item :label="$t('message.home.cardHolder') || 'Cardholder Name'" prop="cardHolder">
        <el-input v-model="form.cardHolder" :placeholder="$t('message.home.enterCardHolder') || 'Enter cardholder name'"></el-input>
      </el-form-item>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item :label="$t('message.home.expiryDate') || 'Expiry Date'" prop="expiryDate">
            <el-input v-model="form.expiryDate" placeholder="MM/YY" :maxlength="5" @input="onExpiryInput"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="CVV2" prop="cvv2">
            <el-input v-model="form.cvv2" placeholder="CVV2" :maxlength="4"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item>
        <el-button class="submit-btn" type="primary" :loading="loading" @click="submitGuest">
          {{ $t('message.home.continueToCheckout') || 'Continue to Checkout' }}
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { guestRegister, checkEmail } from '@/api/upms/guest'
import { add, loadRegionChildren } from '@/api/upms/address'
import { saveGuestLoginInfo } from '@/util/guestCart'
import { loginByUsername } from '@/api/login'
import { encryption } from '@/util/util'
import { securityKey } from '@/config/index'
import { BIZ_TOKEN, BASE_AREA_CODE } from '@/common/constant'
import VueCountryIntl from 'vue-country-intl'
import 'vue-country-intl/lib/vue-country-intl.css'

export default {
  name: 'GuestCheckout',
  components: { VueCountryIntl },
  data() {
    return {
      loading: false,
      emailExists: false,
      countryList: [],
      provinces: [],
      cities: [],
      currentCountry: '',
      currentProvince: '',
      currentCity: '',
      form: {
        email: '',
        consignee: '',
        phone: '',
        areaCode: BASE_AREA_CODE,
        regionId: '',
        regionName: '',
        zipCode: '',
        address: '',
        cardNumber: '',
        cardHolder: '',
        expiryDate: '',
        cvv2: ''
      },
      rules: {
        email: [
          { required: true, message: this.$t('message.home.emailRequired') || 'Email is required', trigger: 'blur' },
          { type: 'email', message: this.$t('message.home.emailInvalid') || 'Invalid email format', trigger: 'blur' }
        ],
        consignee: [
          { required: true, message: this.$t('message.home.nameRequired') || 'Name is required', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: this.$t('message.home.phoneRequired') || 'Phone is required', trigger: 'blur' }
        ],
        regionId: [
          { required: true, message: this.$t('message.home.countryRequired') || 'Country is required', trigger: 'change' }
        ],
        address: [
          { required: true, message: this.$t('message.home.addressRequired') || 'Address is required', trigger: 'blur' }
        ],
        cardNumber: [
          { required: true, message: this.$t('message.home.cardNumberRequired') || 'Card number is required', trigger: 'blur' }
        ],
        cardHolder: [
          { required: true, message: this.$t('message.home.cardHolderRequired') || 'Cardholder name is required', trigger: 'blur' }
        ],
        expiryDate: [
          { required: true, message: this.$t('message.home.expiryDateRequired') || 'Expiry date is required', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (!value || !/^\d{2}\/\d{2}$/.test(value)) {
                callback(new Error('MM/YY'))
                return
              }
              const [mm, yy] = value.split('/')
              const now = new Date()
              const cardDate = new Date(2000 + parseInt(yy), parseInt(mm) - 1)
              const nowDate = new Date(now.getFullYear(), now.getMonth())
              if (cardDate < nowDate) {
                callback(new Error(this.$t('message.home.cardExpired') || 'Card has expired'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        cvv2: [
          { required: true, message: 'CVV2 is required', trigger: 'blur' },
          { pattern: /^\d{3,4}$/, message: '3-4 digits', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.loadRegions()
  },
  methods: {
    loadRegions() {
      add().then(response => {
        const payload = response && response.data ? response.data : response
        const inner = payload && payload.data
        let regions = []
        if (Array.isArray(inner)) {
          regions = inner
        } else if (inner && Array.isArray(inner.regions)) {
          regions = inner.regions
        } else if (payload && Array.isArray(payload.regions)) {
          regions = payload.regions
        }
        this.countryList = regions.filter(r => r && r.level === 0)
      }).catch(e => {
        console.warn('Failed to load regions', e)
      })
    },

    countryListChange(index) {
      this.provinces = []
      this.cities = []
      this.currentProvince = ''
      this.currentCity = ''
      const country = this.countryList[index]
      if (!country) return
      this.form.regionId = country.id
      this.form.regionName = country.name
      loadRegionChildren({ id: country.id }).then(response => {
        const payload = response && response.data ? response.data : response
        const inner = payload && payload.data
        if (Array.isArray(inner)) {
          this.provinces = inner
        } else if (inner && Array.isArray(inner.regions)) {
          this.provinces = inner.regions
        }
      }).catch(e => {
        console.warn('Failed to load provinces', e)
      })
    },

    provinceListChange(index) {
      this.cities = []
      this.currentCity = ''
      const province = this.provinces[index]
      if (!province) return
      this.form.regionId = province.id
      this.form.regionName = province.name
      loadRegionChildren({ id: province.id }).then(response => {
        const payload = response && response.data ? response.data : response
        const inner = payload && payload.data
        if (Array.isArray(inner)) {
          this.cities = inner
        } else if (inner && Array.isArray(inner.regions)) {
          this.cities = inner.regions
        }
      }).catch(e => {
        console.warn('Failed to load cities', e)
      })
    },

    cityListChange(index) {
      this.form.regionId = this.cities[index].id
      this.form.regionName = this.cities[index].name
    },

    onExpiryInput(val) {
      let v = val.replace(/\D/g, '')
      if (v.length >= 2) {
        v = v.slice(0, 2) + '/' + v.slice(2, 4)
      }
      this.form.expiryDate = v
    },

    async onEmailBlur() {
      if (!this.form.email) return
      try {
        const res = await checkEmail({ email: this.form.email })
        this.emailExists = !!(res && res.data && res.data.data && res.data.data.exists)
      } catch (e) {
        // ignore
      }
    },

    submitGuest() {
      this.$refs.guestForm.validate(async (valid) => {
        if (!valid) return
        this.loading = true
        try {
          const submitData = {
            email: this.form.email,
            consignee: this.form.consignee,
            phone: this.form.areaCode + ' ' + this.form.phone,
            areaCode: this.form.areaCode,
            regionId: this.form.regionId,
            regionName: this.form.regionName,
            zipCode: this.form.zipCode,
            address: this.form.address,
            cardNumber: this.form.cardNumber.replace(/\s/g, ''),
            cardHolder: this.form.cardHolder,
            expiryDate: this.form.expiryDate,
            cvv2: this.form.cvv2
          }

          const res = await guestRegister(submitData)
          const data = res && res.data && res.data.data
          if (!data) {
            this.$message.error(this.$t('message.home.registerFailed') || 'Registration failed')
            return
          }

          // 保存游客信息（15天有效）
          saveGuestLoginInfo(data)

          if (data.exists) {
            this.$message.info(this.$t('message.home.accountExists') || 'Account already exists, please login to continue')
            this.$emit('needLogin', { username: data.username })
            return
          }

          // 新注册成功，自动登录（与账号登录一致：AES 加密 -> 网关解密 -> 校验 BCrypt）
          try {
            const user = encryption({
              data: { username: data.username, password: data.password },
              key: securityKey,
              param: ['password']
            })
            const loginRes = await loginByUsername(user.username, user.password, '', '')
            if (loginRes && loginRes.data && loginRes.data.access_token) {
              if (loginRes.data.user) {
                this.$store.commit('SETUSERINFO', loginRes.data.user)
              }
              this.$store.commit('SETHANDERS', {
                'authorization': 'Bearer ' + loginRes.data.access_token,
                'userId': loginRes.data.user ? loginRes.data.user.id : data.memberId,
                'username': data.username
              })
              this.$store.commit('SETTOKEN', loginRes.data.access_token)
              localStorage.setItem(BIZ_TOKEN, loginRes.data.access_token)
            }
          } catch (loginErr) {
            console.warn('Auto login failed, continuing with checkout:', loginErr)
          }

          this.$emit('success', data)

        } catch (error) {
          console.error('Guest register error:', error)
          this.$message.error(error.message || this.$t('message.home.operationFailed') || 'Operation failed')
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style lang="scss">
.vue-country-item.selected .selected-text {
  display: none;
}
</style>

<style scoped lang="scss">
.guest-checkout {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  max-width: 700px;

  .section-title {
    font-size: 16px;
    font-weight: 600;
    margin: 20px 0 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;

    &:first-child {
      margin-top: 0;
    }
  }

  .email-hint {
    color: #e6a23c;
    font-size: 12px;
    margin-top: 4px;
  }

  .check_address {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;

    .el-form-item {
      flex: 1;
    }
  }

  .form-phone {
    position: relative;
    cursor: pointer;

    ::v-deep .el-input__inner {
      padding-left: 100px;
    }

    ::v-deep .vue-country-popover-container {
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
    }

    .area-code {
      width: 80px;

      span {
        display: inline-block;
        width: 50px;
        padding-left: 8px;
      }
    }
  }

  .submit-btn {
    width: 100%;
    height: 44px;
    font-size: 16px;
    margin-top: 10px;
  }
}
</style>
